import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd, resetShimmer } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** True while the drawer was opened ahead of an add-to-cart request that has not answered yet. */
  #pendingAdd = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean, addedKey?: string } }>} event
   */
  #handleCartAdd = (event) => {
    // A failed add keeps the error visible on the product form instead of covering it.
    if (event.detail.data?.didError) {
      this.cancelPending();
      return;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
    this.#finishPending(event.detail.data?.addedKey);
  };

  /**
   * Opens the drawer before the add-to-cart request answers. Shows a placeholder line where the
   * product will appear, or marks the line that is about to grow when the variant is already in
   * the cart. The real content morphs in when the request answers.
   * @param {string} [variantId] - The variant being added.
   * @returns {boolean} Whether the drawer was opened here (false when it is already open).
   */
  showPending(variantId) {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return false;

    const existingRow = variantId
      ? dialog.querySelector(`.cart-items__table-row[data-key^="${CSS.escape(variantId)}:"]`)
      : null;

    this.#pendingAdd = true;
    dialog.setAttribute('data-pending', existingRow ? 'update' : 'add');
    existingRow?.classList.add('cart-items__table-row--pending');
    dialog.querySelector('text-component[ref="cartTotal"]')?.setAttribute('shimmer', '');

    this.showDialog();
    return true;
  }

  /**
   * The add failed: close the drawer again if it was opened ahead of the request.
   */
  cancelPending() {
    if (!this.#pendingAdd) return;

    this.#pendingAdd = false;
    this.#clearPendingState();
    this.closeDialog();
  }

  /**
   * The add succeeded: drop the placeholder and highlight the line that was added.
   * @param {string} [addedKey] - The cart line key returned by /cart/add.
   */
  #finishPending(addedKey) {
    this.#pendingAdd = false;

    // The cart content morphs in during the same event dispatch; wait a frame so the new line exists.
    requestAnimationFrame(() => {
      this.#clearPendingState();

      if (!addedKey) return;
      const row = this.refs.dialog?.querySelector(`.cart-items__table-row[data-key="${CSS.escape(addedKey)}"]`);
      if (!row) return;

      row.classList.add('cart-items__table-row--added');
      row.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      onAnimationEnd(row, () => row.classList.remove('cart-items__table-row--added'), { subtree: false });
    });
  }

  #clearPendingState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.removeAttribute('data-pending');
    dialog.querySelectorAll('.cart-items__table-row--pending').forEach((row) => {
      row.classList.remove('cart-items__table-row--pending');
    });
    resetShimmer(dialog);
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
